import { destinationFolder, storageOf } from '../core/vault-folders.js';
import { assertCreationQuota, assertDocQuota, forgetQuotaPending } from "../services/quota-state.js";
import { getStorageInfo } from "../services/storage-service.js";
import { t } from "../../shared/i18n/index.js";
/**
 * VAULT STORAGE MODULE
 * Handles loading, saving, and managing the vault index and docs
 *
 * CRDT Integration:
 * When User CRDT is enabled, the vault index (folders, docs, keypair)
 * is stored in the User CRDT instead of the records API. This provides real-time
 * sync across devices.
 */

import { reportSyncPause, markSyncKeyVerified } from '../sync/sync-pause.js';
import { Vault } from "./vault.js";
import * as UserCRDT from "../sync/user-crdt-bridge.js";
import { on, emit, Events } from "../core/events.js";
import { deleteDocCache, flushPendingWrites, deleteStoredMapAttachments, readVaultFile, writeVaultFile } from "../storage/index.js";
import { markKnownOnline, isKnownOnline, forgetKnownOnline, knownOnlineElsewhere, knownEntry } from "../sync/known-online.js";
import { forgetUnsynced, changeMark, markConfirmedIfUnchanged } from "../sync/unsynced-changes.js";
import { ownerProofKey, ownerProof } from "./map-owner-proof.js";

function bytesToBase64(bytes) {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function importAesKey(bytes) {
  return crypto.subtle.importKey("raw", bytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

// Moves online that have not finished (docs/plans/2026-09-29-attachments-
// move-with-their-map.md, stage 2 step 5), kept on this device: the next
// attempt uses the same online key (files already sent stay valid) and is
// offered at the next start. The key is stored wrapped for the account's key
// pair, as in the vault list.
const MOVES_ONLINE_KEY = "orbital-moves-online";

function readMoveRecords() {
  try { return JSON.parse(localStorage.getItem(MOVES_ONLINE_KEY) || "{}") || {}; } catch { return {}; }
}

function readMoveRecord(docId, publicKey) {
  const record = readMoveRecords()[docId];
  return record && record.publicKey === publicKey && record.wrappedDEK ? record : null;
}

/** @returns {boolean} whether it was saved */
function writeMoveRecord(docId, record) {
  try {
    localStorage.setItem(MOVES_ONLINE_KEY, JSON.stringify({ ...readMoveRecords(), [docId]: record }));
    return true;
  } catch {
    return false;
  }
}

function clearMoveRecord(docId) {
  const records = readMoveRecords();
  if (!(docId in records)) return;
  delete records[docId];
  try { localStorage.setItem(MOVES_ONLINE_KEY, JSON.stringify(records)); } catch { /* left: harmless */ }
}

// Dependencies injected at runtime
let _getMasterKey = null;
let _getTemplateData = null;

// User CRDT integration state
let _userCRDTEnabled = false;
let _userCRDTEventCleanup = null;

// ----------------------------------------------------------------
// Registering this account's online maps (docs/architecture/sync.md,
// "Vault repair"): the server's list of the account's maps is the
// reference. A map is registered with its wrapped key and its owner proof
// in one request (one transaction on the server), before the vault lists
// it; it is deleted on the server before it leaves the vault.
// ----------------------------------------------------------------

// Failure bound for a registration (liveness: past it, the server counts
// as unreachable).
const REGISTER_BOUND_MS = 10000;

/** This account's owner proof for a map, or null without the master key. */
async function ownerProofOf(docId) {
  const masterKey = _getMasterKey?.();
  if (!(masterKey instanceof Uint8Array)) return null;
  return ownerProof(await ownerProofKey(masterKey), docId);
}

/**
 * The fields of a registration (POST /api/docs) for a map of this account:
 * its wrapped key (only fills a missing one on the server) and its proof.
 */
async function registrationFields(docId, wrappedDEK) {
  const proof = await ownerProofOf(docId);
  // A legacy entry's key ({ v, iv, ct }, wrapped with the master key) is not sent.
  return { ...(typeof wrappedDEK === "string" && wrappedDEK && { wrapped_dek: wrappedDEK }), ...(proof && { owner_proof: proof }) };
}

/** The server cannot be reached: offline, no answer in time, or no backend behind the proxy. */
const unreachable = (status) => status === 502 || status === 503 || status === 504;

/**
 * Register a new map of this account.
 * @returns {Promise<Response|null>} the answer, or null when the server could not be reached
 */
async function registerNewMap(docId, wrappedDEK, extra = {}) {
  if (globalThis.navigator?.onLine === false) return null;
  const body = { doc_id: docId, ...(await registrationFields(docId, wrappedDEK)), ...extra };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REGISTER_BOUND_MS);
  try {
    const response = await fetch("/api/docs", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return unreachable(response.status) ? null : response;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Initialize VaultStorage with required dependencies
 * @param {Object} deps - Dependencies object
 * @param {Function} deps.getMasterKey - Function that returns the current master key
 * @param {Function} deps.getTemplateData - Function that returns template data for new maps
 */

// ----------------------------------------------------------------
// Internal helpers (Phase 5c)
// ----------------------------------------------------------------

/**
 * Pick the default storage slot for new operations when the caller
 * didn't specify. Online wins when both slots are loaded (signed-in
 * users create shared docs by default); local is the only choice
 * otherwise.
 *
 * @returns {"local" | "online"}
 */
function _defaultStorage() {
  if (UserCRDT.isOnlineEnabled?.()) return "online";
  return "local";
}

/**
 * Ensure a local slot has a "My Maps" root folder, and re-home any
 * docs whose folderId points to nothing into that root.
 *
 * Two cases this addresses:
 * 1. Fresh local slot — no folders yet. Without a root, `createDoc`
 *    sets `folderId: null` and docs never appear under any folder.
 * 2. Pre-existing local slot from before this fix — has docs but no
 *    folders. Without re-homing, those docs stay orphaned.
 *
 * Online slots are server-driven and not touched here.
 *
 * @param {Object} userCRDT - The user-crdt-bridge module
 * @param {string} reason - For logging
 * @param {"local"|"online"} [storage] - Slot to seed (default: active)
 */
function _ensureLocalRootFolder(userCRDT, reason, storage) {
  const slot = storage || "local";
  // The slot's Y.Map structure (folders / docs / keypair maps) must
  // exist before any setFolder / setDocEntry call — otherwise
  // `this.folders.set(...)` throws on an undefined target. For brand-
  // new slots enableUserCRDT calls initStructure explicitly, but
  // existing local CRDTs from earlier app versions may have docs in
  // the Y.Map without the folders sibling. initStructure is
  // idempotent (no-op when all three Y.Maps already exist), so it's
  // safe to call unconditionally here.
  try {
    userCRDT.initStructure?.(`ensure-root-${reason}`, slot);
  } catch (err) {
    console.warn("[VaultStorage] initStructure (pre-seed) failed:", err);
    return;
  }
  const folders = userCRDT.getAllFolders?.(slot) || [];
  let rootId = folders.find((f) => f.parentId === null)?.id;

  if (!rootId) {
    rootId = Vault.generateDocId();
    userCRDT.setFolder({
      id: rootId,
      parentId: null,
      name: t('vault.myMaps'),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }, slot);
    console.log(`[VaultStorage] Seeded default root folder (${reason}, storage=${slot}):`, rootId);
  }

  // Re-home orphans: docs whose folderId doesn't match any folder id.
  const knownFolderIds = new Set(folders.map((f) => f.id));
  knownFolderIds.add(rootId);
  const allDocs = slot === "local"
    ? (userCRDT.getAllLocalDocEntries?.() || [])
    : (userCRDT.getAllOnlineDocEntries?.() || []);
  let rehomed = 0;
  for (const doc of allDocs) {
    if (!doc.folderId || !knownFolderIds.has(doc.folderId)) {
      userCRDT.setDocEntry({ ...doc, folderId: rootId, updatedAt: Date.now() }, slot);
      rehomed++;
    }
  }
  if (rehomed > 0) {
    console.log(`[VaultStorage] Re-homed ${rehomed} orphan doc(s) into root folder (${reason}, storage=${slot})`);
  }
}

export function initVaultStorage(deps) {
  _getMasterKey = deps.getMasterKey;
  _getTemplateData = deps.getTemplateData;
}

/**
 * Register owned online documents; local-only IDs never leave the device.
 * The server says which exist: this device learns them as online, and one it
 * knew that is gone (moved to another device, or deleted there) is reported
 * (Events.MAPS_GONE_ONLINE), including one of this account that left the
 * vault since (its copy here was never removed).
 */
export async function trackOwnedDocuments(index, userId = null) {
  const allDocs = index?.docs?.filter(d => d.role === 'owner' && d._storage === 'online') || [];
  const left = knownOnlineElsewhere(userId, (index?.docs || []).map(d => d.docId));
  const docIds = [...allDocs.map(d => d.docId).filter(Boolean), ...left];
  if (docIds.length > 0) {
    try {
      const trackResponse = await fetch("/api/user/track-documents", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "include",
        body: JSON.stringify({ doc_ids: docIds }),
      });
      if (trackResponse.ok) {
        const result = await trackResponse.json();
        if (result.tracked > 0) {
          console.log(`[VaultStorage] Batch tracked ${result.tracked} documents (${result.already_tracked} already tracked)`);
        }
        // An older server lists nothing: then nothing is concluded.
        if (Array.isArray(result.existing)) {
          const existing = new Set(result.existing);
          markKnownOnline(allDocs.filter((d) => existing.has(d.docId)).map((d) => ({ docId: d.docId, title: d.title })), userId);
          const gone = docIds.filter((docId) => !existing.has(docId) && isKnownOnline(docId));
          if (gone.length) emit(Events.MAPS_GONE_ONLINE, { docIds: gone });
        }
      }
    } catch (err) {
      // Non-fatal - documents will be tracked when opened
      console.warn("[VaultStorage] Batch document tracking failed (will track on open):", err.message);
    }
  }
}

/**
 * Enable User CRDT integration for vault index sync
 * Call this after User CRDT is initialized
 *
 * @param {string} userId - The user ID
 * @param {CryptoKey} masterKey - The master key for encryption
 * @param {Object} options - Options
 * @param {boolean} options.isNewUser - True if this is a new user registration
 * @param {boolean} options.skipSync - True to skip WebSocket sync (for logged-out/offline mode)
 * @returns {Promise<{synced: boolean, hasData: boolean}>}
 */
export async function enableUserCRDT(userId, masterKey, options = {}) {
  const { isNewUser = false, skipSync = false, storage } = options;
  console.log("[VaultStorage] enableUserCRDT starting for user:", userId, { isNewUser, skipSync, storage });

  // Phase 5c: pass storage through to the bridge. When unspecified,
  // the bridge infers from the userId prefix (local-<uuid> → local,
  // anything else → online).
  await UserCRDT.init(userId, masterKey, { isNewUser, storage });
  console.log("[VaultStorage] User CRDT initialized");

  let synced = false;
  let hasData = false;

  if (skipSync) {
    // Local-only mode (logged-out/offline) - just check if we have data in IndexedDB
    hasData = UserCRDT.hasData();
    console.log("[VaultStorage] User CRDT skip sync mode, hasData:", hasData);

    // In logged-out/offline mode, we need to ensure the structure exists
    // (normally the server confirmation would trigger this)
    if (!hasData) {
      console.log("[VaultStorage] Logged-out mode: initializing vault structure");
      UserCRDT.initStructure("logged-out-mode-no-data");
    }
  } else {
    // Start sync and wait for initial data
    const syncResult = await UserCRDT.startSync(10000);
    synced = syncResult.synced;
    hasData = syncResult.hasData;
    console.log("[VaultStorage] User CRDT sync complete:", { synced, hasData });

    // If sync failed entirely we don't know the real server state; the vault
    // proceeds with whatever is local (possibly empty). Server-side data is
    // protected by the user-WS gates: sync-step2 aborts on undecryptable
    // baselines and compaction only fires after local deltas were sent.
    if (!synced && !hasData) {
      console.warn("[VaultStorage] User CRDT sync failed and no local data — vault may render empty until sync recovers");
    }
  }

  _userCRDTEnabled = true;

  // Listen for remote updates (only if sync is enabled)
  if (!skipSync) {
    _userCRDTEventCleanup = on(Events.USER_CRDT_UPDATED, () => {
      // Refresh in-memory index from CRDT
      VaultStorage._refreshIndexFromCRDT();
    });
  }

  // Refresh in-memory index from CRDT
  VaultStorage._refreshIndexFromCRDT();

  // Local-only IDs are never submitted for server ownership tracking.
  if (!skipSync) await trackOwnedDocuments(VaultStorage._index, userId);


  // Load the device-local vault alongside the account in both runtimes.
  // A restored browser session bypasses logged-out startup, so relying on
  // that startup to load local maps would hide them after a signed-in reload.
  const resolvedStorage =
    storage || (typeof userId === "string" && userId.startsWith("local-") ? "local" : "online");
  if (resolvedStorage === "online" && !UserCRDT.isLocalEnabled?.()) {
    try {
      const { getOrCreateLocalUser } = await import("../auth/handlers.js");
      const localUser = getOrCreateLocalUser();
      console.log("[VaultStorage] Online mode: also loading local slot for", localUser.user_id);
      await UserCRDT.initLocal(localUser.user_id);
      if (!UserCRDT.hasLocalData()) {
        UserCRDT.initStructure("dual-crdt-local-bootstrap", "local");
      }
      // Refresh again so the local entries are merged into the index.
      VaultStorage._refreshIndexFromCRDT();
    } catch (err) {
      console.warn("[VaultStorage] Could not load local slot alongside online:", err);
    }
  }

  // Heal the local slot: ensure a "My Maps" root folder exists and
  // any orphan docs (folderId pointing to nothing) get re-homed into
  // it. Runs whenever the local slot is initialized — covers fresh
  // Tauri launches, signed-in dual-CRDT sessions, and existing local
  // users whose docs were created before the root-folder bootstrap
  // existed.
  if (UserCRDT.isLocalEnabled?.()) {
    try {
      _ensureLocalRootFolder(UserCRDT, "enableUserCRDT", "local");
      VaultStorage._refreshIndexFromCRDT();
    } catch (err) {
      console.warn("[VaultStorage] Could not heal local root folder:", err);
    }
  }

  console.log(`[VaultStorage] User CRDT enabled: synced=${synced}, hasData=${hasData}, skipSync=${skipSync}, storage=${resolvedStorage}`);
  return { synced, hasData };
}

/**
 * Disable User CRDT integration (on logout).
 *
 * Phase 5c: destroys the online slot only by default. The local slot
 * stays alive so local docs remain visible across sign-out. Pass
 * `{ destroyLocal: true }` to tear everything down (used at app
 * shutdown, account deletion, etc.).
 */
export async function disableUserCRDT(options = {}) {
  const { destroyLocal = false } = options;
  if (_userCRDTEventCleanup) {
    _userCRDTEventCleanup();
    _userCRDTEventCleanup = null;
  }

  if (destroyLocal) {
    await UserCRDT.destroy();
  } else {
    await UserCRDT.destroyOnline();
  }

  // _userCRDTEnabled stays true if the local slot is still alive —
  // the rest of vault-storage routes through UserCRDT regardless of
  // which slot owns each doc.
  _userCRDTEnabled = UserCRDT.isInitialized();
}

// Removed (Phase 5c cleanup): `enableLocalUserCRDT` was unused — the
// auto-load-local logic embedded in enableUserCRDT() below covers
// every code path that needs it.

/**
 * Check if User CRDT is enabled
 */
export function isUserCRDTEnabled() {
  return _userCRDTEnabled;
}

// Cross-tab DEK invalidation. When one tab handles a rotation it posts here
// so other tabs of the same user invalidate their cached DEK *before* the
// WebSocket `dek-rotated` broadcast reaches them. This is a UX optimization
// — the real defense against stale-wrap uploads is the server-side
// dek_fingerprint CAS on /blobs and /blob-refs/link — but it narrows the
// in-memory race window between rotation and broadcast arrival.
//
// Wrapped in a try because some embeddings (e.g. older Tauri webviews) lack
// BroadcastChannel; the safety net just no-ops in that case.
let _dekChannel = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    _dekChannel = new BroadcastChannel('orbital-dek-invalidate');
  }
} catch {
  _dekChannel = null;
}

/**
 * Notify all other tabs of this user that the DEK for a doc has been
 * invalidated (rotation initiated, or this tab just refreshed from server).
 * No-op when BroadcastChannel isn't available.
 */
function _broadcastDekInvalidated(docId) {
  if (!_dekChannel || !docId) return;
  try {
    _dekChannel.postMessage({ type: 'dek-invalidated', docId, ts: Date.now() });
  } catch {
    /* channel may be closed during teardown — no-op */
  }
}

if (_dekChannel) {
  _dekChannel.addEventListener('message', (ev) => {
    const msg = ev?.data;
    if (!msg || msg.type !== 'dek-invalidated' || !msg.docId) return;
    // Peer tab handled a rotation. Drop our cached unwrap so the next
    // getDocDEK re-reads the wrappedDEK from vault (the User-CRDT sync
    // pushes the updated wrappedDEK to all tabs separately). Pass
    // broadcast:false so we don't echo this back at the sender.
    const cached = VaultStorage._dekCache.get(msg.docId);
    if (cached instanceof Uint8Array) cached.fill(0);
    VaultStorage._dekCache.delete(msg.docId);
  });
}

/**
 * Phase 5d: surface dual-slot state to UI consumers. The vault list
 * uses this to decide whether to render the "Promote to online"
 * button (only meaningful when the user has an online account
 * available to promote into).
 */
export function isOnlineSlotEnabled() {
  return UserCRDT.isOnlineEnabled?.() === true;
}

// Called before a deleted map's local cache is removed, so a linked-map
// channel still open on it can stop first (its open storage would block or
// recreate the cache). Registered by the CRDT bridge.
let _beforeDocCacheDelete = null;
export function setBeforeDocCacheDelete(fn) {
  _beforeDocCacheDelete = fn;
}

export function isLocalSlotEnabled() {
  return UserCRDT.isLocalEnabled?.() === true;
}

export const VaultStorage = {
  // Maps being moved online right now, in this tab (promoteToOnline).
  _movesRunning: new Set(),
  // In-memory cache
  _index: null, // Decrypted index
  _dekCache: new Map(), // docId -> DEK (unwrapped keys), max 100 entries (LRU)
  _DEK_CACHE_MAX: 100,
  _currentDocId: null, // Currently open map docId
  _indexDirty: false, // True if index needs saving

  /**
   * Store a DEK in the cache with LRU eviction (max 100 entries).
   * When the cache exceeds the max, the oldest entry (first key in Map
   * iteration order) is evicted.
   */
  _setDEKCache(docId, dek) {
    // Delete first so re-insertion moves it to the end (most-recently-used)
    this._dekCache.delete(docId);
    this._dekCache.set(docId, dek);
    if (this._dekCache.size > this._DEK_CACHE_MAX) {
      // Evict the oldest entry (first key in iteration order), zeroing key material
      const oldest = this._dekCache.keys().next().value;
      const evicted = this._dekCache.get(oldest);
      if (evicted instanceof Uint8Array) evicted.fill(0);
      this._dekCache.delete(oldest);
    }
  },

  // ============================================
  // User CRDT Integration Helpers
  // ============================================

  /**
   * Refresh in-memory index from User CRDT(s).
   *
   * Phase 5c: merges entries from both local and online slots. Each
   * entry is tagged with `_storage: "local" | "online"` so callers
   * can filter/group without consulting the bridge. The merged
   * `folders` and `docs` arrays preserve back-compat for vault UI
   * code that reads them flat; dual-section UI uses the tag.
   */
  _refreshIndexFromCRDT() {
    if (!_userCRDTEnabled) return;

    if (!this._index) {
      this._index = Vault.createEmptyIndex();
    }

    // Merge folders/docs from both slots, tagging each with its
    // owning slot. The local slot may still be loading, or the
    // online slot may be absent (logged out / Tauri local-only).
    const localFolders = (UserCRDT.getAllLocalFolders?.() || []).map((f) => ({
      ...f,
      _storage: "local",
    }));
    const onlineFolders = (UserCRDT.getAllOnlineFolders?.() || []).map((f) => ({
      ...f,
      _storage: "online",
    }));
    const localDocs = (UserCRDT.getAllLocalDocEntries?.() || []).map((d) => ({
      ...d,
      // Local docs are always owned by the local user — there's no sharing
      // concept in local-only mode. Default the role so downstream filters
      // that use `d.role === "owner"` (vault list, search, export) treat
      // them as user-owned. Without this they fall into the "shared" bucket
      // by negation and are hidden from the main vault list.
      role: d.role || "owner",
      _storage: "local",
    }));
    const onlineDocs = (UserCRDT.getAllOnlineDocEntries?.() || []).map((d) => ({
      ...d,
      _storage: "online",
    }));

    const previousDocs = new Map((this._index.docs || []).map(doc => [doc.docId, doc]));
    for (const doc of [...onlineDocs, ...localDocs]) {
      if (doc.wrappedDEK !== previousDocs.get(doc.docId)?.wrappedDEK || doc._keyAuthorityConflict) {
        this.clearDEKCache(doc.docId, { broadcast: false });
      }
    }
    this._index.folders = [...onlineFolders, ...localFolders];
    this._index.docs = [...onlineDocs, ...localDocs];

    // Keypair only exists on the online slot (used to wrap DEKs for
    // sharing); for web offline mode the bridge falls back to the
    // active slot, which is the local one there.
    const keypair = UserCRDT.getKeypair();
    if (keypair) {
      this._index.keypair = keypair;
    }

    console.log("[VaultStorage] Refreshed index from CRDT:", {
      folders: this._index.folders.length,
      docs: this._index.docs.length,
      localFolders: localFolders.length,
      onlineFolders: onlineFolders.length,
      localDocs: localDocs.length,
      onlineDocs: onlineDocs.length,
    });

    emit(Events.VAULT_INDEX_UPDATED, { source: 'remote' });
  },

  // Load and decrypt the vault index
  // With CRDT-only architecture, this just refreshes from User CRDT
  async loadIndex() {
    // User CRDT is the source of truth for the vault index
    if (_userCRDTEnabled) {
      this._refreshIndexFromCRDT();
      this._indexDirty = false;
      return this._index;
    }

    // Not authenticated - no index available
    console.log("[VaultStorage] loadIndex: User CRDT not enabled, no index");
    return null;
  },

  // Save the vault index
  // With CRDT-only architecture, this is handled by User CRDT auto-sync
  async saveIndex() {
    // User CRDT auto-syncs, no explicit save needed
    if (_userCRDTEnabled) {
      this._indexDirty = false;
      console.log("[VaultStorage] Index saved via User CRDT (auto-sync)");
      return;
    }

    // Not authenticated - cannot save
    console.warn("[VaultStorage] saveIndex: User CRDT not enabled, cannot save");
  },

  // Get the current index
  getIndex() {
    return this._index;
  },


  // In-memory storage for pending initial states (new maps not yet synced to CRDT)
  // Capped to prevent unbounded memory growth during bulk import
  _pendingInitialStates: new Map(),
  _PENDING_STATES_MAX: 500,

  _setPendingInitialState(docId, state) {
    this._pendingInitialStates.set(docId, state);
    if (this._pendingInitialStates.size > this._PENDING_STATES_MAX) {
      const oldest = this._pendingInitialStates.keys().next().value;
      console.warn(`[VaultStorage] Pending initial states exceeded ${this._PENDING_STATES_MAX}, evicting oldest: ${oldest}`);
      this._pendingInitialStates.delete(oldest);
    }
  },

  /**
   * Create a new map
   *
   * NOTE: Map content is NOT saved to legacy records anymore.
   * Instead, the initial state is stored in memory and synced to CRDT
   * when the map is first opened. This ensures all map data goes through
   * the CRDT sync system for consistency.
   *
   * @param {string} title - Map title
   * @param {string} folderId - Parent folder ID (or null for root)
   * @param {Object} initialState - Optional initial state (for import)
   * @returns {Promise<{docId: string, initialState: Object}>}
   */
  async createMap(title, folderId, initialState = null, extraFields = null) {
    return this.createDoc(title, folderId, initialState, extraFields);
  },

  /**
   * Store pending initial state for a newly created or shared document
   * Used to populate the CRDT when the doc is first opened
   * @param {string} docId - Document ID
   * @param {Object} state - { data: treeData }
   */
  setPendingInitialState(docId, state) {
    this._setPendingInitialState(docId, state);
  },

  /**
   * Check if a pending initial state exists for a document (non-consuming)
   * @param {string} docId
   * @returns {boolean}
   */
  hasPendingInitialState(docId) {
    return this._pendingInitialStates.has(docId);
  },

  /**
   * Get pending initial state for a newly created map
   * Returns and clears the pending state (one-time use)
   *
   * @param {string} docId - Document ID
   * @returns {Object|null} Initial state or null if not pending
   */
  getPendingInitialState(docId) {
    const state = this._pendingInitialStates.get(docId);
    if (state) {
      this._pendingInitialStates.delete(docId);
      return state;
    }
    return null;
  },


  // Rename a map (index only)
  async renameMap(docId, newTitle) {
    assertDocQuota(docId);
    if (!this._index) throw new Error("Vault not initialized");

    const docEntry = this._index.docs?.find(d => d.docId === docId);
    if (!docEntry) throw new Error(`Doc not found: ${docId}`);

    docEntry.title = newTitle;
    docEntry.updatedAt = Date.now();
    if (_userCRDTEnabled) UserCRDT.setDocEntry(docEntry);
    this._indexDirty = true;
    await this.saveIndex();
  },

  // Move a map to a different folder (index only)
  async moveMap(docId, newFolderId) {
    if (!this._index) throw new Error("Vault not initialized");

    const docEntry = this._index.docs?.find(d => d.docId === docId);
    if (!docEntry) throw new Error(`Doc not found: ${docId}`);

    const target = destinationFolder(this._index, docEntry, newFolderId);
    if (docEntry.role && docEntry.role !== 'owner') throw new Error(t('vault.storageError.onlyOwnMaps'));
    docEntry.folderId = target.id;
    docEntry.updatedAt = Date.now();
    if (_userCRDTEnabled) UserCRDT.setDocEntry(docEntry);
    this._indexDirty = true;
    await this.saveIndex();
  },

  // Delete a map
  async deleteMap(docId) {
    return this.deleteDoc(docId);
  },

  async ensureRootFolder(storage = _defaultStorage()) {
    if (!this._index) throw new Error('Vault not initialized');
    if (storage === 'local' && !isLocalSlotEnabled()) throw new Error(t('vault.storageError.openLocal'));
    if (storage === 'online' && !isOnlineSlotEnabled()) throw new Error(t('vault.storageError.signIn'));
    const existing = this._index.folders.find(f => f.parentId === null && storageOf(f) === storage);
    if (existing) return existing.id;
    const root = { id: Vault.generateDocId(), name: t('vault.myMaps'), parentId: null,
      createdAt: Date.now(), updatedAt: Date.now() };
    this._index.folders.push({ ...root, _storage: storage });
    if (_userCRDTEnabled) UserCRDT.setFolder(root, storage);
    await this.saveIndex();
    return root.id;
  },

  // Create a folder
  async createFolder(name, parentId = null, { storage: requestedStorage } = {}) {
    if (!this._index) throw new Error("Vault not initialized");

    const now = Date.now();
    const parent = parentId ? this._index.folders.find(f => f.id === parentId) : null;
    if (parentId && !parent) throw new Error(t('vault.storageError.parentGone'));
    const storage = requestedStorage || (parent ? storageOf(parent) : _defaultStorage());
    if (!['local', 'online'].includes(storage)) throw new Error('Unsupported vault storage type');
    if (parent && storageOf(parent) !== storage) throw new Error(t('vault.storageError.parentSameVault'));
    if (storage === 'local' && !isLocalSlotEnabled()) throw new Error(t('vault.storageError.localFolders'));

    const rootId = parentId ? null : await this.ensureRootFolder(storage);

    const folder = {
      id: Vault.generateDocId(),
      parentId: parentId || rootId,
      name: name,
      createdAt: now,
      updatedAt: now,
    };

    this._index.folders.push({ ...folder, _storage: storage });

    // Update User CRDT if enabled
    if (_userCRDTEnabled) {
      UserCRDT.setFolder(folder, storage);
    }

    await this.saveIndex();
    return folder.id;
  },

  // Rename a folder
  async renameFolder(folderId, newName) {
    if (!this._index) throw new Error("Vault not initialized");

    const folder = this._index.folders.find((f) => f.id === folderId);
    if (!folder) throw new Error(`Folder not found: ${folderId}`);

    folder.name = newName;
    folder.updatedAt = Date.now();

    // Update User CRDT if enabled
    if (_userCRDTEnabled) {
      UserCRDT.setFolder(folder, storageOf(folder));
    }

    await this.saveIndex();
  },

  async moveFolder(folderId, parentId) {
    if (!this._index) throw new Error('Vault not initialized');
    const folder = this._index.folders.find(f => f.id === folderId);
    if (!folder || folder.parentId === null) throw new Error(t('vault.storageError.rootMove'));
    const target = destinationFolder(this._index, folder, parentId, { movingFolder: true });
    folder.parentId = target.id;
    folder.updatedAt = Date.now();
    if (_userCRDTEnabled) UserCRDT.setFolder(folder, storageOf(folder));
    await this.saveIndex();
  },

  // Delete a folder (must be empty or move contents)
  async deleteFolder(folderId, moveContentsTo = null) {
    if (!this._index) throw new Error("Vault not initialized");

    // Check if it's the root folder
    const folder = this._index.folders.find((f) => f.id === folderId);
    if (!folder) throw new Error(`Folder not found: ${folderId}`);
    if (folder.parentId === null) {
      throw new Error("Cannot delete root folder");
    }

    // Move or check contents
    const docsInFolder = (this._index.docs || []).filter(
      (d) => d.folderId === folderId,
    );
    const subfolders = this._index.folders.filter(
      (f) => f.parentId === folderId,
    );

    if (docsInFolder.length > 0 || subfolders.length > 0) {
      if (!moveContentsTo) {
        throw new Error("Folder not empty. Specify moveContentsTo.");
      }
      // Validate before touching any map or child folder.
      destinationFolder(this._index, folder, moveContentsTo, { movingFolder: true });
      // Move docs
      for (const doc of docsInFolder) {
        doc.folderId = moveContentsTo;
        doc.updatedAt = Date.now();
        if (_userCRDTEnabled) {
          UserCRDT.setDocEntry(doc);
        }
      }
      // Move subfolders
      for (const sub of subfolders) {
        sub.parentId = moveContentsTo;
        sub.updatedAt = Date.now();
        if (_userCRDTEnabled) {
          UserCRDT.setFolder(sub, storageOf(folder));
        }
      }
    }
    this._indexDirty = true;

    // Remove folder
    const idx = this._index.folders.findIndex((f) => f.id === folderId);
    this._index.folders.splice(idx, 1);

    // Update User CRDT if enabled
    if (_userCRDTEnabled) {
      UserCRDT.deleteFolder(folderId, storageOf(folder));
    }

    await this.saveIndex();
  },

  // Get current docId
  getCurrentDocId() {
    return this._currentDocId;
  },

  // Set current docId (for shared docs that bypass loadMap)
  setCurrentDocId(docId) {
    this._currentDocId = docId;
  },

  /**
   * Clear the cached unwrapped DEK for a specific doc.
   * Call after a DEK rotation so the next access re-unwraps the new wrapped DEK.
   *
   * Also broadcasts on the cross-tab channel so peer tabs of the same user
   * drop their cached DEK without waiting for the WebSocket `dek-rotated`
   * broadcast. Skipped when called from the cross-tab receive path (would
   * echo forever).
   *
   * @param {string} docId
   * @param {Object} [opts]
   * @param {boolean} [opts.broadcast=true] - emit on the cross-tab channel.
   */
  clearDEKCache(docId, opts = {}) {
    const { broadcast = true } = opts;
    const dek = this._dekCache.get(docId);
    if (dek instanceof Uint8Array) dek.fill(0);
    this._dekCache.delete(docId);
    if (broadcast) _broadcastDekInvalidated(docId);
  },

  // Clear all cached state (on logout)
  clear() {
    this._index = null;
    // Zero out DEK key material before clearing (best-effort memory scrub)
    for (const dek of this._dekCache.values()) {
      if (dek instanceof Uint8Array) {
        dek.fill(0);
      }
    }
    this._dekCache.clear();
    this._currentDocId = null;
    this._indexDirty = false;
    this._pendingInitialStates.clear();
  },

  // ============================================
  // Keypair Management (for sharing)
  // ============================================

  /**
   * Get the stored keypair from the index
   * @returns {Promise<{publicKey: string, secretKey: string, serverSynced: boolean}|null>}
   */
  async getKeypair() {
    if (!this._index) {
      await this.loadIndex();
    }
    // The user CRDT is the synced source of truth; the index copy is a local
    // cache that can lag behind it.
    if (_userCRDTEnabled) {
      const synced = UserCRDT.getKeypair();
      if (synced) return synced;
    }
    return this._index?.keypair || null;
  },

  /**
   * The vault keypair that wraps online map keys. A new one is generated only
   * for an account the server has no key for: if one is published but this
   * device's vault doesn't hold it yet, generating would replace the account's
   * identity on every device (see KeypairManager.initialize).
   */
  async _keypairForOnlineDocs() {
    if (this._index?.keypair) return this._index.keypair;
    let published;
    try {
      const response = await fetch("/api/keypair", { credentials: "include" });
      published = response.status === 404 ? null : response.ok ? (await response.json()).public_key : undefined;
    } catch {
      published = undefined;
    }
    if (published !== null) {
      throw new Error(t('vault.storageError.keyMissing'));
    }
    const { Keypair } = await import("./keypair.js");
    const kp = await Keypair.generate();
    const keypairData = {
      publicKey: Keypair.toBase64(kp.publicKey),
      secretKey: Keypair.toBase64(kp.secretKey),
      serverSynced: false,
    };
    await this.setKeypair(keypairData);
    return keypairData;
  },

  /**
   * Store the keypair in the index (encrypted with master key)
   * @param {Object} keypair - {publicKey: string, secretKey: string, serverSynced: boolean}
   */
  async setKeypair(keypair) {
    if (!this._index) {
      throw new Error("Vault not initialized");
    }

    this._index.keypair = keypair;
    this._indexDirty = true;

    // Update User CRDT if enabled
    if (_userCRDTEnabled) {
      UserCRDT.setKeypair(keypair);
    }

    await this.saveIndex();
  },

  /**
   * Update the wrapped DEK for a document after DEK rotation.
   * @param {string} docId
   * @param {string} newWrappedDEKBase64 - Base64 encoded wrapped DEK
   */
  async updateDocWrappedDEK(docId, newWrappedDEKBase64, { verifiedAuthority = null } = {}) {
    let doc = this.getDoc(docId);
    if (!doc) {
      console.error(`[VaultStorage] updateDocWrappedDEK: doc not found: ${docId}`);
      return;
    }

    if (!verifiedAuthority) {
      const { verifyReplacement } = await import('./map-key-trust.js');
      verifiedAuthority = await verifyReplacement(docId, newWrappedDEKBase64);
    }
    doc = this.getDoc(docId);
    if (!doc) throw new Error('Document changed while verifying its key; retry sync.');
    if (doc.keyAuthority && (!verifiedAuthority || doc.keyAuthority.epoch > verifiedAuthority.epoch ||
        (doc.keyAuthority.epoch === verifiedAuthority.epoch && doc.keyAuthority.id !== verifiedAuthority.id))) {
      throw new Error('Map key changed concurrently. Local data and keys are retained; retry sync.');
    }
    if (doc.wrappedDEK && doc.wrappedDEK !== newWrappedDEKBase64) {
      doc.previousWrappedDEKs = [...new Set([...(doc.previousWrappedDEKs || []), doc.wrappedDEK])];
    }
    if (verifiedAuthority) doc.keyAuthority = verifiedAuthority;
    doc.wrappedDEK = newWrappedDEKBase64;
    this.clearDEKCache(docId);
    doc.updatedAt = Date.now();
    if (_userCRDTEnabled) {
      UserCRDT.setDocEntry(doc);
    }

    this._indexDirty = true;
    try {
      await this.saveIndex();
    } catch (e) {
      console.error("[VaultStorage] Failed to save after DEK rotation:", e);
      throw e; // Propagate so caller knows the rotation save failed
    }
    markSyncKeyVerified(docId);
    // Same reason as setDocEntry: surface the change so crdt-bridge can
    // retry ensureLinkedDocSync if a previously-failed linked doc just
    // got a usable wrap (e.g. after self-heal swapped in the server's
    // authoritative wrap).
    emit(Events.VAULT_INDEX_UPDATED, { source: 'local' });
  },

  /**
   * Hex SHA-256 of the doc's `wrappedDEK` bytes — used as a CAS token by
   * the upload (`/blobs`) and cross-doc link (`/blob-refs/link`) endpoints
   * so the server can detect an in-flight DEK rotation and reject a write
   * that would otherwise land with a wrapping keyed to the now-stale DEK.
   *
   * Stays in sync with `_dekCache` by virtue of every wrappedDEK update
   * (rotation, setDocEntry, reconcileWrappedDEKWithServer) calling
   * `clearDEKCache(docId)` — so the cached raw DEK is always the unwrap
   * of the current `wrappedDEK` (or absent, forcing a re-unwrap on next
   * `getDocDEK`).
   *
   * Returns null when the doc has no wrappedDEK (caller treats as "no
   * CAS info available" → server skips the check).
   *
   * @param {string} docId
   * @returns {Promise<string|null>}
   */
  async getDocDEKFingerprint(docId) {
    const doc = this.getDoc(docId);
    if (!doc?.wrappedDEK) return null;
    // Decode the base64 wrappedDEK to bytes; server hashes the raw `bytea`
    // column. Both ends must hash the same byte sequence — going through
    // base64 on the client and back on the server would not.
    const binary = atob(doc.wrappedDEK);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const hashBuf = await crypto.subtle.digest('SHA-256', bytes);
    const hashBytes = new Uint8Array(hashBuf);
    let hex = '';
    for (let i = 0; i < hashBytes.length; i++) {
      hex += hashBytes[i].toString(16).padStart(2, '0');
    }
    return hex;
  },

  /**
   * Force-refresh the DEK for a doc by re-fetching the server's wrapped_dek
   * and re-unwrapping it. Used after a 409 `dek_stale` response when a
   * concurrent rotation invalidated the cached DEK.
   *
   * Returns the new raw DEK bytes (32-byte Uint8Array) on success, or null
   * if the doc was revoked / fetch failed (caller treats as fatal).
   *
   * Side effects: `wrappedDEK` in the vault index is updated to the
   * server's value, and `_dekCache[docId]` is replaced with the fresh
   * unwrap.
   *
   * @param {string} docId
   * @returns {Promise<Uint8Array|null>}
   */
  async refreshDocDek(docId) {
    const updated = await this.reconcileWrappedDEKWithServer(docId);
    // reconcileWrappedDEKWithServer already clears the DEK cache on
    // update, so the next getDocDEK will re-unwrap the new wrapping.
    // If `updated` is false the wrap was already current — still safe to
    // call getDocDEK; it'll either return cached or re-unwrap the same.
    void updated;
    try {
      return await this.getDocDEK(docId);
    } catch (e) {
      console.warn('[VaultStorage] refreshDocDek: getDocDEK failed:', e);
      return null;
    }
  },

  /**
   * Reconcile local wrapped DEK against the server's authoritative copy
   * in `doc_keys`. Used by paths that are about to expose the DEK to
   * other users (invite/grant) so we never wrap a stale DEK and ship it
   * to a recipient that won't be able to decrypt the server's baseline.
   *
   * Returns true if the local wrap was updated, false if it already
   * matched the server (or the server has no wrap to compare against).
   * Network/auth failures are treated as "nothing to reconcile" — the
   * caller proceeds with whatever local state it had.
   *
   * @param {string} docId
   * @returns {Promise<boolean>}
   */
  async reconcileWrappedDEKWithServer(docId) {
    // A map kept by its link: the server keeps no copy of its key for us.
    if (this.getDoc(docId)?.link) return false;
    let resp;
    try {
      resp = await fetch(`/api/shares/${docId}/my-dek`, { credentials: "include" });
    } catch (err) {
      console.warn("[VaultStorage] reconcileWrappedDEK: network error:", err);
      return false;
    }
    if (!resp.ok) return false;
    let body;
    try { body = await resp.json(); } catch { return false; }
    const { wrapped_dek, is_revoked } = body || {};
    if (!wrapped_dek || is_revoked) return false;
    const localDoc = this.getDoc(docId);
    if (!localDoc) return false;
    if (localDoc.wrappedDEK === wrapped_dek) return false;
    console.log("[VaultStorage] reconcileWrappedDEK: local wrap stale, updating from server:", docId);
    await this.updateDocWrappedDEK(docId, wrapped_dek);
    this.clearDEKCache(docId);
    return true;
  },

  // ============================================
  // Doc Operations
  // ============================================

  /**
   * Get a doc entry from the unified docs[] index
   * @param {string} docId
   * @returns {Object|null}
   */
  /** The key and owner proof to send with this map's registration (document-loader). */
  registrationFields(docId) {
    return registrationFields(docId, this.getDoc(docId)?.wrappedDEK);
  },

  getDoc(docId) {
    return this._index?.docs?.find(d => d.docId === docId) || null;
  },

  /**
   * Get the DEK for any document (personal or shared).
   * Uses keypair-based unwrapping.
   *
   * Returns a COPY of the cached bytes: the cache zeroes its arrays in
   * place on eviction / rotation / cross-tab invalidation, so handing out
   * the cached reference meant a caller holding the bytes across one of
   * those events would silently end up encrypting with an all-zero key —
   * ciphertext nothing can ever decrypt.
   *
   * @param {string} docId
   * @returns {Promise<Uint8Array>}
   */
  /**
   * The key of a map stored on this device, for its attachments. A map on
   * this device gets one at its first attachment (`create`); it is saved to
   * disk before it is returned, so no file is ever encrypted with a key that
   * a crash could lose. Kept in the device's map list, next to the data: not
   * a protection (encryption.md, "Data on the device"), it keeps one format
   * with online maps so moving a map only rewraps small keys.
   * @returns {Promise<Uint8Array|null>} null when the map has none and `create` is false
   */
  async getLocalDocKey(docId, { create = false } = {}) {
    if (UserCRDT.getDocStorage?.(docId) !== "local") {
      throw new Error(`Not a map on this device: ${docId}`);
    }
    const existing = UserCRDT.getLocalMapKey(docId);
    if (existing) return base64ToBytes(existing);
    if (!create) return null;
    let pending = this._localKeyPending.get(docId);
    if (!pending) {
      pending = (async () => {
        const inForce = UserCRDT.setLocalMapKey(docId, bytesToBase64(Vault.generateDEK()));
        await flushPendingWrites();
        return inForce;
      })().finally(() => this._localKeyPending.delete(docId));
      this._localKeyPending.set(docId, pending);
    }
    return base64ToBytes(await pending);
  },

  _localKeyPending: new Map(),

  /**
   * The key protecting a map's attachments, wherever the map is stored:
   * its device key for a map on this device, its DEK otherwise.
   */
  async getAttachmentKeyBytes(docId, { create = false } = {}) {
    if (UserCRDT.getDocStorage?.(docId) === "local") return this.getLocalDocKey(docId, { create });
    return this.getDocDEK(docId);
  },

  async getDocDEK(docId) {
    if (this.getDoc(docId)?._keyAuthorityConflict) {
      reportSyncPause('map', docId, 'conflict');
      throw new Error('Conflicting map key histories. Local data and all keys are retained; sync is paused.');
    }
    // Check cache first
    const cached = this._dekCache.get(docId);
    if (cached) return cached.slice();

    const doc = this.getDoc(docId);
    if (!doc || !doc.wrappedDEK) {
      throw new Error(`Doc not found or missing wrappedDEK: ${docId}`);
    }

    const { Keypair } = await import("./keypair.js");
    const keypairData = this._index?.keypair;
    if (!keypairData) throw new Error("No keypair available");

    const wrappedDEK = Keypair.fromBase64(doc.wrappedDEK);
    const secretKey = Keypair.fromBase64(keypairData.secretKey);
    const dek = await Keypair.unwrapDEKAnonymous(wrappedDEK, secretKey);
    secretKey.fill(0); // Zero secret key after use (defense-in-depth)
    this._setDEKCache(docId, dek);
    return dek.slice();
  },

  /**
   * Unwrap a map key wrapped for this account (base64, as `doc_keys` and
   * vault entries hold it), for a map the vault does not list yet ("Find
   * missing maps": a map the server keeps for the account). Raw bytes; the
   * caller zeroes them after use.
   * @param {string} wrappedDEK
   * @returns {Promise<Uint8Array>}
   */
  async unwrapAccountDEK(wrappedDEK) {
    const { Keypair } = await import("./keypair.js");
    const keypairData = this._index?.keypair;
    if (!keypairData) throw new Error("No keypair available");
    const secretKey = Keypair.fromBase64(keypairData.secretKey);
    try {
      return await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(wrappedDEK), secretKey);
    } finally {
      secretKey.fill(0);
    }
  },

  /**
   * Create a new doc using the unified model (keypair-wrapped DEK)
   *
   * An online map is registered on the server (with its key and owner
   * proof) before the vault lists it. When the server cannot be reached,
   * `whenOffline: "device"` keeps it on this device instead, marked to go
   * online (`goesOnline`, moved by interfaces/classic/ui/map-storage.js once the server can be
   * reached); without it, creating an online map needs the connection.
   *
   * @param {string} title
   * @param {string} folderId
   * @param {Object} initialState
   * @param {Object} extraFields - storage, makeCurrent, whenOffline; the rest goes into the vault entry
   */
  async createDoc(title, folderId, initialState = null, extraFields = null) {
    // Phase 5c: per-doc storage routing. Local docs skip keypair/DEK
    // generation entirely — they have no encryption layer and exist
    // only on disk (in Tauri) or in IndexedDB (in web).
    const { storage: requestedStorage, makeCurrent = true, whenOffline = null, ...otherExtras } = extraFields || {};
    const selectedFolder = this._index?.folders.find(f => f.id === folderId);
    let storage = requestedStorage || (selectedFolder ? storageOf(selectedFolder) : _defaultStorage());
    if (!['local', 'online'].includes(storage)) throw new Error('Unsupported vault storage type');
    if (folderId && !selectedFolder) throw new Error(t('vault.storageError.destinationGone'));
    if (selectedFolder && storageOf(selectedFolder) !== storage) throw new Error(t('vault.storageError.folderSameVault'));
    if (storage === 'local' && !isLocalSlotEnabled()) throw new Error(t('vault.storageError.localMaps'));
    if (storage === 'online') {
      try { await getStorageInfo({ force: true }); } catch { /* Retain the last known quota offline. */ }
      assertCreationQuota();
    }

    const docId = Vault.generateDocId();
    const now = Date.now();

    let wrappedDEK = null;
    let dek = null;
    let goesOnline = null;

    if (storage === "online") {
      // Online docs need a keypair-wrapped DEK so the doc can be
      // shared with other users later.
      const { Keypair } = await import("./keypair.js");
      const keypairData = await this._keypairForOnlineDocs();
      dek = Vault.generateDEK();
      const ownPubKey = Keypair.fromBase64(keypairData.publicKey);
      const wrappedDEKBytes = await Keypair.wrapDEKAnonymous(dek, ownPubKey);
      wrappedDEK = Keypair.toBase64(wrappedDEKBytes);

      // On the server first, then in the vault.
      const response = await registerNewMap(docId, wrappedDEK,
        otherExtras.recoveredFromShareId ? { recovered_from: otherExtras.recoveredFromShareId } : {});
      if (!response) {
        if (whenOffline !== "device" || !isLocalSlotEnabled()) {
          dek.fill(0);
          throw new Error(t('vault.storageError.createNeedsConnection'));
        }
        // On this device until the server can be reached. Same id: if the
        // request did reach the server, the move online finds the map
        // registered as this account's.
        dek.fill(0);
        dek = null;
        wrappedDEK = null;
        storage = "local";
        goesOnline = { folderId: folderId || this._index.folders.find(f => f.parentId === null && storageOf(f) === "online")?.id || null };
        folderId = null;
      } else if (response.status === 413) {
        dek.fill(0);
        throw Object.assign(new Error(t('vault.quota.createBlocked')), { code: 'quota_exceeded' });
      } else if (!response.ok) {
        dek.fill(0);
        throw new Error(t('vault.promoteError.serverStatus', { status: response.status }));
      } else {
        markKnownOnline([{ docId, title }]);
      }
    }

    const templateData = _getTemplateData();
    const state = initialState || {
      data: templateData,
      ganttScale: "auto",
      ganttZoom: 1,
      ganttRowHeight: 100,
      mindmapGap: 50,
      focusId: templateData.id,
      selectedId: templateData.id,
      view: "dashboard",
      userSetProjectDates: false,
      hideCompleted: false,
    };
    state.data.name = title;

    // Local entries omit wrappedDEK entirely (no encryption layer).
    // role is set to "owner" so vault-list filters that key on
    // `role === "owner"` don't bucket local docs into "Shared".
    // `_refreshIndexFromCRDT` applies the same default when reading
    // back from the CRDT; this keeps the in-memory entry consistent
    // immediately, before any refresh.
    const docEntry = {
      docId,
      title,
      folderId: folderId || this._index.folders.find(f => f.parentId === null && storageOf(f) === storage)?.id || null,
      createdAt: now,
      updatedAt: now,
      meta: { pinned: false, color: null },
      role: "owner",
      ...(storage === "online" ? { wrappedDEK } : {}),
      ...(goesOnline && { goesOnline }),
      ...otherExtras,
    };

    if (!this._index.docs) this._index.docs = [];
    this._index.docs.push({ ...docEntry, _storage: storage });

    if (_userCRDTEnabled) {
      // Explicit storage tells the bridge which slot to write to.
      UserCRDT.setDocEntry(docEntry, storage);
    }

    await this.saveIndex();
    this._setPendingInitialState(docId, state);
    if (dek) this._setDEKCache(docId, dek);
    if (makeCurrent) this._currentDocId = docId;
    if (goesOnline) emit(Events.MAP_KEPT_ON_DEVICE, { docId, title });

    return { docId, initialState: this._pendingInitialStates.get(docId) };
  },

  /**
   * Delete a doc from the unified docs[] model
   * @param {string} docId
   */
  async deleteDoc(docId) {
    if (!this._index) throw new Error("Vault not initialized");

    const idx = this._index.docs?.findIndex(d => d.docId === docId);
    if (idx === undefined || idx === -1) throw new Error(`Doc not found: ${docId}`);

    // Server deletion first (only owners should DELETE the doc from
    // the server). Phase 5c: local docs never made it to the server,
    // so skip the network round-trip entirely.
    const docEntry = this._index.docs[idx];
    const isLocalDoc = docEntry._storage === "local";
    if (!isLocalDoc && (!docEntry.role || docEntry.role === "owner")) {
      // Deleted on the server first, then here: never only here (the
      // server's list is the reference, docs/architecture/sync.md "Vault
      // repair"). Without the server, nothing is deleted.
      if (globalThis.navigator?.onLine === false) throw new Error(t('vault.storageError.deleteNeedsConnection'));
      // The server closes the map's sockets while it deletes it, this
      // device's too ("map_deleted"), maybe before it answers: forgotten
      // first, so that close does not read as "deleted on another device".
      const known = knownEntry(docId);
      forgetKnownOnline(docId);
      const stillOnline = (error) => {
        if (known) markKnownOnline([{ docId, title: known.title }], known.account);
        return error;
      };
      let response;
      try {
        response = await fetch(`/api/docs/${docId}`, {
          method: "DELETE",
          credentials: "include",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
      } catch {
        throw stillOnline(new Error(t('vault.storageError.deleteNeedsConnection')));
      }
      if (response.ok || response.status === 204 || response.status === 404) {
        console.log("[VaultStorage] Deleted document from server:", docId);
      } else if (unreachable(response.status)) {
        throw stillOnline(new Error(t('vault.storageError.deleteNeedsConnection')));
      } else if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        throw stillOnline(new Error(`Server rejected deletion (document has active shares): ${data.error || "conflict"}`));
      } else {
        throw stillOnline(new Error(`Server returned ${response.status} when deleting document`));
      }
    } else if (isLocalDoc) {
      // A move online that stopped left a partial copy on the server: it
      // goes with the map.
      // (The record stays if that fails: nothing else ever deletes it.)
      if (docId in readMoveRecords()) {
        try {
          const response = await fetch(`/api/docs/${docId}`, { method: "DELETE", credentials: "include", headers: { "X-Requested-With": "XMLHttpRequest" } });
          if (response.ok || response.status === 404) clearMoveRecord(docId);
        } catch (err) {
          console.warn("[VaultStorage] Partial online copy not deleted:", err);
        }
      }
    } else {
      // Non-owner: use leave endpoint instead of DELETE, or just clean up locally
      // (the server-side membership was already cleaned up by revocation/leave)
      console.log("[VaultStorage] Non-owner doc, skipping server DELETE:", docId);
    }

    this._index.docs.splice(idx, 1);
    forgetQuotaPending(docId);
    forgetKnownOnline(docId);
    forgetUnsynced([docId]);
    if (_userCRDTEnabled) UserCRDT.deleteDocEntry(docId);

    // Drop references to the deleted doc from every other entry's
    // linkedIntoDocs list so the link-tracking index stays coherent after
    // direct deletes (vault browser "delete map", account deletion, etc.).
    // Without this, revocation-migration later tries to queue pending
    // migrations onto a host doc that no longer exists.
    if (this._index.docs) {
      for (let i = 0; i < this._index.docs.length; i++) {
        const entry = this._index.docs[i];
        if (entry.linkedIntoDocs?.includes(docId)) {
          const next = entry.linkedIntoDocs.filter((id) => id !== docId);
          this._index.docs[i] = {
            ...entry,
            linkedIntoDocs: next,
            updatedAt: Date.now(),
          };
          if (_userCRDTEnabled) UserCRDT.setDocEntry(this._index.docs[i]);
        }
      }
    }

    await this.saveIndex();
    this.clearDEKCache(docId); // M-13: zero key material before deletion

    try {
      await _beforeDocCacheDelete?.(docId);
    } catch (err) {
      console.warn("[VaultStorage] Could not close the map before deleting its cache:", err);
    }
    try {
      await deleteDocCache(docId);
    } catch (err) {
      console.warn("[VaultStorage] Failed to delete local doc cache:", err);
    }
    // Its attachments on this device go with it: a map on this device's own
    // files, or an online map's offline copies.
    try {
      await deleteStoredMapAttachments(isLocalDoc ? "device" : "offline", docId);
    } catch (err) {
      console.warn("[VaultStorage] Failed to delete the map's attachments on this device:", err);
    }
  },

  /**
   * Add or update a doc entry in the unified docs[] model.
   * Used when accepting an invitation or updating role from server.
   * @param {Object} docEntry - DocEntry object (must include docId, role, wrappedDEK)
   */
  async setDocEntry(docEntry) {
    if (!this._index) throw new Error("Vault not initialized");
    if (!this._index.docs) this._index.docs = [];

    const existing = this._index.docs.findIndex(d => d.docId === docEntry.docId);
    const previous = this._index.docs[existing];
    // A map kept by its link (engine/sync/link-sharing.js) is checked through
    // the link's key log, not a member's: its key arrives verified.
    if (previous?.wrappedDEK && docEntry.wrappedDEK && previous.wrappedDEK !== docEntry.wrappedDEK && !docEntry.link) {
      const { verifyReplacement } = await import('./map-key-trust.js');
      const authority = await verifyReplacement(docEntry.docId, docEntry.wrappedDEK);
      docEntry = { ...docEntry,
        ...(authority && { keyAuthority: authority }),
        previousWrappedDEKs: [...new Set([...(previous.previousWrappedDEKs || []), previous.wrappedDEK])],
      };
    }
    const now = Date.now();
    const entry = { ...docEntry, updatedAt: now };
    if (existing >= 0) {
      this._index.docs[existing] = { ...this._index.docs[existing], ...entry };
    } else {
      this._index.docs.push({ createdAt: now, ...entry });
    }

    // Invalidate DEK cache so it re-unwraps with new wrappedDEK if changed
    this.clearDEKCache(docEntry.docId);

    if (_userCRDTEnabled) UserCRDT.setDocEntry(this._index.docs.find(d => d.docId === docEntry.docId));
    this._indexDirty = true;
    await this.saveIndex();

    // Notify listeners that the vault index changed locally. crdt-bridge's
    // VAULT_INDEX_UPDATED handler turns a freshly-arrived wrappedDEK into
    // an immediate ensureLinkedDocSync call so the linked-doc stub flips
    // from no-access to loaded without waiting for a page refresh. Without
    // this emit, only remote User-CRDT updates trigger the refresh path.
    emit(Events.VAULT_INDEX_UPDATED, { source: 'local' });
  },

  /**
   * Record that `hostDocId` now contains a stub to `linkedDocId`. Stored on
   * the linked doc's vault entry as `linkedIntoDocs: string[]`. Used by
   * share-revocation to know which of the user's other docs need stub
   * migration when the linked share is revoked and a recovered doc is
   * created — much cheaper than scanning every vault doc at revocation time.
   *
   * Safe if `linkedDocId` is one the user can't actually open (e.g. stale
   * stub): we just create/update a vault entry with the linkedIntoDocs list;
   * the rest of the entry stays absent.
   */
  async recordLink(linkedDocId, hostDocId) {
    if (!linkedDocId || !hostDocId) return;
    if (!this._index) return;
    const existing = this._index.docs?.findIndex(d => d.docId === linkedDocId) ?? -1;
    if (existing < 0) return; // Only track for docs we actually have access to
    const prev = this._index.docs[existing].linkedIntoDocs || [];
    if (prev.includes(hostDocId)) return;
    const next = [...prev, hostDocId];
    this._index.docs[existing] = {
      ...this._index.docs[existing],
      linkedIntoDocs: next,
      updatedAt: Date.now(),
    };
    if (_userCRDTEnabled) UserCRDT.setDocEntry(this._index.docs[existing]);
    this._indexDirty = true;
    await this.saveIndex();
  },

  /**
   * Inverse of `recordLink`. Call after `unlinkDocument` if no other stub
   * in the host doc still references the linked one.
   */
  async removeLink(linkedDocId, hostDocId) {
    if (!linkedDocId || !hostDocId) return;
    if (!this._index) return;
    const existing = this._index.docs?.findIndex(d => d.docId === linkedDocId) ?? -1;
    if (existing < 0) return;
    const prev = this._index.docs[existing].linkedIntoDocs || [];
    if (!prev.includes(hostDocId)) return;
    const next = prev.filter(id => id !== hostDocId);
    this._index.docs[existing] = {
      ...this._index.docs[existing],
      linkedIntoDocs: next,
      updatedAt: Date.now(),
    };
    if (_userCRDTEnabled) UserCRDT.setDocEntry(this._index.docs[existing]);
    this._indexDirty = true;
    await this.saveIndex();
  },

  /**
   * List host doc ids that are known to link to `linkedDocId`.
   * @returns {string[]}
   */
  getLinkers(linkedDocId) {
    const doc = this.getDoc(linkedDocId);
    return doc?.linkedIntoDocs || [];
  },

  /**
   * Queue a stub migration for a doc that isn't currently loaded. Stored on
   * the host doc's vault entry as `pendingStubMigrations: { oldDocId:
   * newDocId }`. Applied next time the doc is opened.
   */
  async queuePendingStubMigration(hostDocId, oldDocId, newDocId) {
    if (!hostDocId || !oldDocId || !newDocId) return;
    if (!this._index) return;
    const existing = this._index.docs?.findIndex(d => d.docId === hostDocId) ?? -1;
    if (existing < 0) return;
    const prev = this._index.docs[existing].pendingStubMigrations || {};
    if (prev[oldDocId] === newDocId) return;
    this._index.docs[existing] = {
      ...this._index.docs[existing],
      pendingStubMigrations: { ...prev, [oldDocId]: newDocId },
      updatedAt: Date.now(),
    };
    if (_userCRDTEnabled) UserCRDT.setDocEntry(this._index.docs[existing]);
    this._indexDirty = true;
    await this.saveIndex();
  },

  /**
   * Consume (read + clear) any pending stub migrations for a host doc.
   * Returns the map `{ oldDocId: newDocId }` or an empty object.
   */
  async consumePendingStubMigrations(hostDocId) {
    if (!hostDocId || !this._index) return {};
    const existing = this._index.docs?.findIndex(d => d.docId === hostDocId) ?? -1;
    if (existing < 0) return {};
    const pending = this._index.docs[existing].pendingStubMigrations;
    if (!pending || Object.keys(pending).length === 0) return {};
    this._index.docs[existing] = {
      ...this._index.docs[existing],
      pendingStubMigrations: {},
      updatedAt: Date.now(),
    };
    if (_userCRDTEnabled) UserCRDT.setDocEntry(this._index.docs[existing]);
    this._indexDirty = true;
    await this.saveIndex();
    return pending;
  },

  // ============================================
  // Phase 5d — per-doc storage transitions
  // ============================================
  //
  // promoteToOnline(docId): MOVE a local doc to the online slot.
  //   - generates a DEK wrapped with the user's keypair (or reuses the one
  //     recorded by an attempt that stopped: see the move records)
  //   - POSTs /api/docs to register ownership on the server
  //   - sends our key, the map's attachments and its content
  //   - only then inserts the online vault entry (role: owner, wrappedDEK)
  //     and deletes the local entry; the device's attachment files become
  //     offline copies
  //   - the .orbital file on disk stays at the same docId
  //
  // duplicateAsLocal(docId): COPY an online doc to a fresh local doc.
  //   - generates a new docId
  //   - reads the online doc's .orbital bytes from the Tauri vault
  //   - writes them to <vault>/<newDocId>.orbital
  //   - inserts a local vault entry (no DEK, no role, owner-rendered
  //     by the role-default in _refreshIndexFromCRDT)
  //   - the original online doc is untouched

  /**
   * Promote a local doc to the online slot. Destructive on the local
   * side — the doc is no longer in the local vault index after this
   * succeeds. Requires the online slot to be loaded (i.e. signed in).
   * On failure the map stays on this device; calling again continues
   * where it stopped. While it runs, its record is not an "unfinished"
   * move (unfinishedMovesOnline).
   *
   * @param {string} docId
   * @returns {Promise<{ ok: boolean, error?: string }>}
   */
  async promoteToOnline(docId, options = {}) {
    this._movesRunning.add(docId);
    try { return await this._promoteToOnline(docId, options); }
    finally { this._movesRunning.delete(docId); }
  },

  async _promoteToOnline(docId, { report = () => {} } = {}) {
    // ---- Validate preconditions before any state change ----
    if (!UserCRDT.isLocalEnabled?.() || !UserCRDT.isOnlineEnabled?.()) {
      return { ok: false, error: t('vault.promoteError.slots') };
    }
    const { Keypair } = await import("./keypair.js");
    // We need an online keypair to wrap the DEK against. Generate one
    // if missing (mirrors createDoc's behavior for first online doc).
    const keypairData = await this._keypairForOnlineDocs();
    const record = readMoveRecord(docId, keypairData.publicKey);
    // Its attachments' key on this device, if it has one (kept after the
    // move: the map's user-CRDT key record is never removed).
    const localKey = UserCRDT.getLocalMapKey(docId);

    // Already online: an earlier attempt switched it and stopped before
    // tidying up (its device files, the move record).
    if (UserCRDT.getDocStorage?.(docId) === "online") {
      if (!record) return { ok: false, error: t('vault.promoteError.notLocal') };
      await this._finishMoveOnline(docId, localKey);
      return { ok: true };
    }
    if (UserCRDT.getDocStorage?.(docId) !== "local") {
      return { ok: false, error: t('vault.promoteError.notLocal') };
    }
    const localEntry = UserCRDT.getDocEntry(docId);
    if (!localEntry) {
      return { ok: false, error: t('vault.promoteError.entryMissing') };
    }
    try { await getStorageInfo({ force: true }); } catch { /* Existing known limit still applies. */ }
    if (!record) assertCreationQuota();

    // ---- The map's online key: the one of the attempt that stopped, if
    // any (its files on the server are wrapped for it), else a new one,
    // recorded on this device before anything is sent. ----
    let dek, wrappedDEK;
    if (record) {
      const secretKey = Keypair.fromBase64(keypairData.secretKey);
      try {
        dek = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(record.wrappedDEK), secretKey);
      } finally {
        secretKey.fill(0);
      }
      wrappedDEK = record.wrappedDEK;
    } else {
      dek = Vault.generateDEK();
      const wrappedDEKBytes = await Keypair.wrapDEKAnonymous(dek, Keypair.fromBase64(keypairData.publicKey));
      wrappedDEK = Keypair.toBase64(wrappedDEKBytes);
    }
    // If it can't be recorded (storage unavailable), the move still works;
    // a later attempt just can't resume and sends everything again.
    if (!record) {
      writeMoveRecord(docId, { wrappedDEK, publicKey: keypairData.publicKey, title: localEntry.title || "", startedAt: Date.now() });
    }
    const resume = !!record;

    // ---- Step 1: server registration, with the key and the owner proof
    // (ours already, when resuming). The vault repair of another device
    // puts a registered map back only once it has content: the content is
    // sent last, and then the map is complete. ----
    try {
      const response = await fetch(`/api/docs`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ doc_id: docId, ...(await registrationFields(docId, wrappedDEK)) }),
      });
      // 201 (created) and 200 (already ours) continue. 409: another user
      // owns this ID on the server (extremely unlikely with UUIDs).
      if (!response.ok) {
        if (response.status === 409) {
          clearMoveRecord(docId);
          return { ok: false, error: t('vault.promoteError.conflict') };
        }
        if (response.status === 401) return { ok: false, error: t('vault.promoteError.unauthenticated') };
        return { ok: false, error: t('vault.promoteError.serverStatus', { status: response.status }) };
      }
    } catch (err) {
      return { ok: false, error: t('vault.promoteError.network', { error: err.message || err }) };
    }

    // ---- Step 2: our key, the map's files and its content go to the
    // server. The map stays on this device (and in its list) until all of
    // it is there: a failure or a closed tab leaves it where it was, and the
    // next attempt continues with the same key. ----
    try {
      const keyResponse = await fetch(`/api/docs/${docId}/keys/me`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ wrapped_dek: wrappedDEK }),
      });
      if (!keyResponse.ok) throw new Error(t('vault.promoteError.serverStatus', { status: keyResponse.status }));
      // Its attachments first (same IDs, keys rewrapped), checked on the server.
      if (localKey) {
        const { uploadDeviceAttachments } = await import("../services/attachment-transfer.js");
        const fingerprintBytes = new Uint8Array(await crypto.subtle.digest("SHA-256", base64ToBytes(wrappedDEK)));
        await uploadDeviceAttachments(docId, {
          fromKey: await importAesKey(base64ToBytes(localKey)),
          toKey: await importAesKey(dek),
          fingerprint: [...fingerprintBytes].map((b) => b.toString(16).padStart(2, "0")).join(""),
          resume,
          report,
        });
      }
      // The content last (the latest edits); it replaces any content an
      // earlier attempt sent.
      report({ label: t('app.mapStorage.sendingMap') });
      const { readLocalMapState, uploadMapBaseline } = await import("./data-export.js");
      const mark = changeMark(docId);
      const { state, warnings } = await readLocalMapState(docId);
      if (warnings.length) throw new Error(warnings.join(" "));
      if (state) await uploadMapBaseline(docId, state, dek);
      // The server has the copy it read: confirmed, unless it changed since.
      markConfirmedIfUnchanged(docId, mark);
    } catch (err) {
      return { ok: false, error: t('vault.promoteError.upload', { error: err.message || err }) };
    }

    // ---- Step 3: the map is online: its online entry, then off the
    // device's list. ----
    const now = Date.now();
    // A map created while offline goes to the folder chosen then, if it
    // still exists.
    const chosenFolder = localEntry.goesOnline?.folderId;
    const onlineFolder = chosenFolder && this._index?.folders?.some((f) => f.id === chosenFolder && storageOf(f) === "online") ? chosenFolder : null;
    const onlineEntry = {
      docId,
      title: localEntry.title,
      // Local & online folder structures aren't merged (per the
      // dual-CRDT design). New online doc lands at root; the user
      // can move it post-promotion using existing rename/move.
      folderId: onlineFolder,
      role: "owner",
      wrappedDEK,
      createdAt: localEntry.createdAt || now,
      updatedAt: now,
      meta: localEntry.meta || { pinned: false, color: null },
    };
    try {
      UserCRDT.setDocEntry(onlineEntry, "online");
    } catch (err) {
      return { ok: false, error: t('vault.promoteError.writeOnline', { error: err.message || err }) };
    }
    this._setDEKCache(docId, dek);
    markKnownOnline([{ docId, title: onlineEntry.title }]);
    await this._finishMoveOnline(docId, localKey);
    console.log(`[VaultStorage] Promoted ${docId} to online`);
    return { ok: true };
  },

  /**
   * The end of a move online, once its online entry exists: off the
   * device's list, its files on this device kept as the online map's
   * offline copies, the move record removed. Repeated safely.
   */
  async _finishMoveOnline(docId, localKey) {
    // Use the explicit-slot delete so we don't accidentally remove the
    // online entry (which online-first lookup resolves to first).
    try {
      UserCRDT.deleteDocEntryFromSlot(docId, "local"); // no-op once removed
    } catch (err) {
      console.warn("[VaultStorage] promoteToOnline: failed to remove local entry:", err);
      // Non-fatal: the map is online; it may show in both sections until
      // the next refresh.
    }
    this._indexDirty = true;
    await this.saveIndex();
    this._refreshIndexFromCRDT();
    clearMoveRecord(docId);
    // The server has its attachments (checked): the device's files become
    // the online map's offline copies.
    if (localKey) {
      try {
        const { keepDeviceFilesAsOfflineCopies } = await import("../services/attachment-transfer.js");
        await keepDeviceFilesAsOfflineCopies(docId, {
          fromKey: await importAesKey(base64ToBytes(localKey)),
          toKey: await importAesKey(await this.getDocDEK(docId)),
        });
      } catch (err) {
        console.warn("[VaultStorage] promoteToOnline: device attachments not converted:", err);
        try { await deleteStoredMapAttachments("device", docId); } catch { /* retried at next delete */ }
      }
    }
  },

  /** Moves online that stopped before the end, for this account (to offer again). */
  unfinishedMovesOnline() {
    const publicKey = this._index?.keypair?.publicKey;
    if (!publicKey) return [];
    return Object.entries(readMoveRecords())
      .filter(([docId, record]) => record?.publicKey === publicKey && !this._movesRunning.has(docId))
      .map(([docId, record]) => ({ docId, title: record.title, startedAt: record.startedAt }));
  },

  /**
   * A map this device knew was online that the server no longer has (moved
   * to another device, or deleted there): remove this device's copy, as
   * deleting it here would, without asking the server. The caller closes it
   * first if it is open. A map this device keeps on the device (moved here)
   * keeps its file and attachments: only a stale online entry goes.
   * Files attached to it and never sent stay (they are reported as not sent).
   */
  async forgetGoneMap(docId) {
    const keptHere = (UserCRDT.getAllLocalDocEntries?.() || []).some((doc) => doc.docId === docId);
    try {
      UserCRDT.deleteDocEntryFromSlot(docId, "online"); // no-op once removed
    } catch (err) {
      console.warn("[VaultStorage] Gone map's online entry not removed:", err);
    }
    forgetQuotaPending(docId);
    this._indexDirty = true;
    await this.saveIndex();
    this._refreshIndexFromCRDT();
    this.clearDEKCache(docId);
    if (keptHere) return;
    try {
      await _beforeDocCacheDelete?.(docId);
    } catch (err) {
      console.warn("[VaultStorage] Could not close the gone map before deleting its copy:", err);
    }
    try {
      await deleteDocCache(docId);
    } catch (err) {
      console.warn("[VaultStorage] Gone map's copy not deleted:", err);
    }
    try {
      await deleteStoredMapAttachments("offline", docId);
    } catch (err) {
      console.warn("[VaultStorage] Gone map's attachment copies not deleted:", err);
    }
    forgetKnownOnline(docId);
    forgetUnsynced([docId]);
  },

  /**
   * Moving an online map to this device (engine/services/move-to-device.js), once
   * its file and attachments are on the device and checked: listed on this
   * device instead of online. The online copy is deleted afterwards by the
   * caller. Repeated safely.
   * @param {string} docId
   * @param {{title?: string, createdAt?: number, meta?: object}} entry the map's online entry
   */
  async listOnThisDevice(docId, entry) {
    const now = Date.now();
    if (!(UserCRDT.getAllLocalDocEntries?.() || []).some((doc) => doc.docId === docId)) {
      UserCRDT.setDocEntry({
        docId,
        title: entry.title,
        // Folders on this device and online are separate (dual-CRDT
        // design): it lands at the root of the maps on this device.
        folderId: null,
        createdAt: entry.createdAt || now,
        updatedAt: now,
        meta: entry.meta || { pinned: false, color: null },
      }, "local");
    }
    // The explicit slot: an online-first lookup would find the online one.
    UserCRDT.deleteDocEntryFromSlot(docId, "online"); // no-op once removed
    forgetQuotaPending(docId);
    this._indexDirty = true;
    await this.saveIndex();
    this._refreshIndexFromCRDT();
  },

  /**
   * Duplicate an online doc as a new local-only doc. Non-destructive
   * — the source online doc is untouched. The copy gets a new docId
   * and its own .orbital file. Tauri-only (web has no filesystem
   * vault to copy into).
   *
   * @param {string} sourceDocId
   * @returns {Promise<{ ok: boolean, newDocId?: string, error?: string }>}
   */
  async duplicateAsLocal(sourceDocId) {
    // ---- Validate ----
    const isTauri =
      typeof window !== "undefined" &&
      typeof window.__TAURI_INTERNALS__ !== "undefined";
    if (!isTauri) {
      return { ok: false, error: t('vault.saveLocalError.desktopOnly') };
    }
    if (!UserCRDT.isLocalEnabled?.()) {
      return { ok: false, error: t('vault.saveLocalError.localNotLoaded') };
    }
    const sourceEntry = UserCRDT.getDocEntry(sourceDocId);
    if (!sourceEntry) {
      return { ok: false, error: t('vault.saveLocalError.sourceMissing') };
    }
    const sourceStorage = UserCRDT.getDocStorage?.(sourceDocId);
    if (sourceStorage !== "online") {
      return { ok: false, error: t('vault.saveLocalError.sourceNotOnline') };
    }

    // ---- Read source bytes from disk ----
    let bytes;
    try {
      bytes = await readVaultFile("main", sourceDocId);
      if (bytes.length === 0) {
        // The doc has no local file yet (not opened since signing in).
        // Without bytes we can't duplicate; ask the user to open the
        // doc first so FilePersistence materializes the file.
        return {
          ok: false,
          error: t('vault.saveLocalError.noLocalFile'),
        };
      }
    } catch (err) {
      return { ok: false, error: t('vault.saveLocalError.readFailed', { error: err.message || err }) };
    }

    // ---- Allocate the new doc ----
    const newDocId = Vault.generateDocId();
    const now = Date.now();
    const newEntry = {
      docId: newDocId,
      title: t('vault.map.localCopyName', { title: sourceEntry.title }),
      folderId: null,
      createdAt: now,
      updatedAt: now,
      meta: sourceEntry.meta || { pinned: false, color: null },
    };

    // ---- Write the new file ----
    try {
      await writeVaultFile("main", newDocId, bytes);
    } catch (err) {
      return { ok: false, error: t('vault.saveLocalError.writeFailed', { error: err.message || err }) };
    }

    // ---- Register in local slot ----
    try {
      UserCRDT.setDocEntry(newEntry, "local");
    } catch (err) {
      // Rollback the file write.
      try { await window.__TAURI_INTERNALS__.invoke("doc_delete", { docId: newDocId }); } catch {}
      return { ok: false, error: t('vault.saveLocalError.registerFailed', { error: err.message || err }) };
    }

    this._indexDirty = true;
    await this.saveIndex();
    this._refreshIndexFromCRDT();

    console.log(`[VaultStorage] Duplicated ${sourceDocId} as local ${newDocId}`);
    return { ok: true, newDocId };
  },
};
